import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IConnectedAccount, ISubscriptionsResponse, IUnifiedSubscription } from '../../../common/purchases';

export type TKnownUserSubscriptionsStatus = 'loading' | 'ready' | 'error';

type TUseKnownUserSubscriptionsArgs = {
  enabled: boolean;
  isAuthenticated: boolean;
  loadSubscriptions: () => Promise<ISubscriptionsResponse>;
};

type TUserSubscriptionsSelectorResult = {
  subscriptions: IUnifiedSubscription[] | null;
  connectedAccounts: IConnectedAccount[] | null;
};

export function useKnownUserSubscriptions(args: TUseKnownUserSubscriptionsArgs) {
  const { enabled, isAuthenticated, loadSubscriptions } = args;
  const { subscriptions: cachedSubscriptions, connectedAccounts: cachedConnectedAccounts } = useSelector(
    (state: any): TUserSubscriptionsSelectorResult => ({
      subscriptions: Array.isArray(state?.userSettings?.subscriptions?.subscriptions)
        ? state.userSettings.subscriptions.subscriptions
        : null,
      connectedAccounts: Array.isArray(state?.userSettings?.subscriptions?.connectedAccounts)
        ? state.userSettings.subscriptions.connectedAccounts
        : null
    })
  );
  const [loadedResult, setLoadedResult] = useState<ISubscriptionsResponse | null>(null);
  const [didLoadFail, setDidLoadFail] = useState(false);

  const refreshSubscriptions = useCallback(async () => {
    setDidLoadFail(false);

    try {
      const result = await loadSubscriptions();
      setLoadedResult(result);
      return result;
    } catch (error) {
      setDidLoadFail(true);
      throw error;
    }
  }, [loadSubscriptions]);

  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      setLoadedResult(null);
      setDidLoadFail(false);
      return;
    }

    if (cachedSubscriptions !== null || loadedResult !== null || didLoadFail) {
      return;
    }

    void refreshSubscriptions().catch(() => undefined);
  }, [cachedSubscriptions, didLoadFail, enabled, isAuthenticated, loadedResult, refreshSubscriptions]);

  const resolvedSubscriptions = loadedResult?.subscriptions || cachedSubscriptions || [];
  const resolvedConnectedAccounts = loadedResult?.connectedAccounts || cachedConnectedAccounts || [];
  const hasKnownSubscriptions = !enabled || !isAuthenticated || cachedSubscriptions !== null || loadedResult !== null;
  const status: TKnownUserSubscriptionsStatus = hasKnownSubscriptions ? 'ready' : didLoadFail ? 'error' : 'loading';

  return {
    subscriptions: resolvedSubscriptions,
    connectedAccounts: resolvedConnectedAccounts,
    status,
    refreshSubscriptions
  };
}
